import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';

import { Skeleton } from 'antd';
import { paths } from 'routes';

import { Role } from 'features/auth/types';
import { useAppSelector } from 'lib/store/rootReducer';
import { LocationDescriptor, LocationDescriptorObject } from 'history';

export interface PrivateRouteProps
  extends Omit<RouteProps, 'children' | 'render'> {
  /**
   * The accepted roles for this route.
   * The `*` value will allow any role.
   */
  roles: Role[] | '*';

  /**
   * A temporary field to allow extra roles to access a route
   */
  temp__extraRoles?: Role[];
  /**
   * If the user is unauthenticated, or the users role does not match any of the roles in the `roles` attribute, then redirect the user to `redirectPath`.
   */
  redirectPath?: LocationDescriptor;
  redirectShouldPreserveSearchQuery?: boolean;
  /**
   * Flags the route is only available when the password is set and the user has their password set
   */
  passwordCheck?: boolean;
}

/**
 * A route configuration that only allows authenticated Users with specific roles to access the content.
 * Cases:
 * 1. The user is unauthenticated. Redirect to `redirectPath`.
 * 2. The user is authenticated and their role is not included in the `roles` list attribute. Redirect to `redirectPath`.
 * 3. The user is authenticated and their role is included in the `roles` list attribute. The user is allowed to access the path.
 * @param props
 * @returns
 */
export function PrivateRoute(props: PrivateRouteProps) {
  const { component: Component, passwordCheck, ...rest } = props;
  const user = useAppSelector(state => state.auth.user);
  const isLoading = useAppSelector(state => state.auth.isUserDataLoading);
  const location = useLocation();

  /**
   * Nothing can be rendered
   */
  if (!Component) return null;

  /**
   * Show a skeleton while the user data is being resolved.
   * At this point we do not know if the user is signed in and authentication data is being fetched, or if the user is signed out
   */
  if (!user && isLoading) return <Skeleton active={true} />;
  /**
   * The user is authenticated, and the user role matches one of the accepted roles
   */
  const userPassesRoleCheck =
    user &&
    (props.roles === '*' ||
      props.roles.includes(user.role) ||
      props.temp__extraRoles?.includes(user.extraRole as any));

  /**
   * Check if the user passes the password check on this route.
   * This is `true` if:
   * 1. The route does not require a password check.
   * 2. The user is signed in, the route requires a passowrd check, and the user has their password set
   */
  const userPassessPasswordCheck =
    !passwordCheck || (user && passwordCheck && !user.needsPassword);
  return (
    <Route
      {...rest}
      render={renderProps => {
        /**
         * Case 1: The user is not signed in
         * Case 2: The user does not pass the role check
         * Case 3: The route and user needs a password to be set
         */
        if (!user || !userPassesRoleCheck || !userPassessPasswordCheck) {
          return (
            <Redirect
              to={{
                pathname: paths.signIn(),
                state: { from: renderProps.location },
                search: props.redirectShouldPreserveSearchQuery
                  ? location.search
                  : '',
                /**
                 * There was no redirectPath configured for this route, and the user is not signed in.
                 * Redirect the user to the patient sign in page
                 */
                ...(rest.redirectPath
                  ? resolveRedirectPath(rest.redirectPath)
                  : undefined)
              }}
            />
          );
        }

        /**
         * Case 4: The user passed all of the criteria for this route
         */
        return <Component {...renderProps} />;
      }}
    />
  );
}

export function resolveRedirectPath(
  redirectPath: LocationDescriptor
): LocationDescriptorObject {
  if (typeof redirectPath === 'string') {
    return {
      pathname: redirectPath
    };
  }

  return redirectPath;
}
